import { FormControl, Grid, Stack, TextField, Typography } from "@mui/material";
import { Field } from "formik";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { soilType } from "../../config/fieldIntialValues";
import CustomTooltip from "shared-components/src/components/tooltip/customTooltip.component";

const SoilTypeFormComponent = (props) => {
  const { t } = useTranslation();
  const { formikProps } = props;
  const { errors, touched, values } = formikProps;

  const textureSumError =
    formikProps?.errors?.soil_type &&
    typeof formikProps.errors.soil_type === "string";

  const soilPorosityValue = values?.soil_type?.soil_porosity;
  const showSoilPorosityWarning = soilPorosityValue && soilPorosityValue < 35;

  return (
    <Fragment>
      <Stack marginY={2}>
        <Stack spacing={1} direction="row">
          <Typography fontWeight="bold" fontSize="16px">
            {t("soilType.title")}
          </Typography>
          <CustomTooltip title={t("soilType.soil_porosity_info")} />
        </Stack>
        <Typography fontSize="14px" color="grey">
          {t("irrigation_form.irrigation_info_text")}
        </Typography>
      </Stack>
      {formikProps?.errors?.soil_type &&
        typeof formikProps.errors.soil_type === "string" && (
          <div style={{ color: "red", marginBottom: 8 }}>
            {formikProps.errors.soil_type}
          </div>
        )}
      <Grid container spacing={2}>
        <Fragment>
          <Grid item xs={12} sm={12} md={6}>
            <FormControl fullWidth>
              <Field
                type="number"
                inputProps={{ min: 0 }}
                as={TextField}
                label={`${t("soilType." + soilType.texture)} ${t(
                  "soilType.clay"
                )} ${t("soilType.unit.clay")}*`}
                name="soil_type.clay"
                variant="outlined"
                fullWidth
                error={
                  textureSumError ||
                  (touched?.soil_type?.clay && Boolean(errors?.soil_type?.clay))
                }
                helperText={
                  touched?.soil_type?.clay && errors?.soil_type?.clay
                    ? errors?.soil_type?.clay
                    : ""
                }
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <FormControl fullWidth>
              <Field
                type="number"
                inputProps={{ min: 0 }}
                as={TextField}
                label={`${t(
                  "soilType." + soilType.permanent_wilting_point
                )} ${t("soilType.unit." + soilType.permanent_wilting_point)}*`}
                name="soil_type.permanent_wilting_point"
                variant="outlined"
                fullWidth
                error={
                  touched?.soil_type?.permanent_wilting_point &&
                  Boolean(errors?.soil_type?.permanent_wilting_point)
                }
                helperText={
                  touched?.soil_type?.permanent_wilting_point &&
                  errors?.soil_type?.permanent_wilting_point
                    ? errors?.soil_type?.permanent_wilting_point
                    : ""
                }
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <FormControl fullWidth>
              <Field
                type="number"
                inputProps={{ min: 0 }}
                as={TextField}
                label={`${t("soilType." + soilType.texture)} ${t(
                  "soilType.sand"
                )} ${t("soilType.unit.sand")}*`}
                name="soil_type.sand"
                variant="outlined"
                fullWidth
                error={
                  textureSumError ||
                  (touched?.soil_type?.sand && Boolean(errors?.soil_type?.sand))
                }
                helperText={
                  touched?.soil_type?.sand && errors?.soil_type?.sand
                    ? errors?.soil_type?.sand
                    : ""
                }
              />
            </FormControl>
          </Grid>
        </Fragment>

        <Fragment>
          <Grid item xs={12} sm={12} md={6}>
            <FormControl fullWidth>
              <Field
                type="number"
                inputProps={{ min: 0 }}
                as={TextField}
                label={`${t("soilType." + soilType.field_capacity)} ${t(
                  "soilType.unit." + soilType.field_capacity
                )}*`}
                name="soil_type.field_capacity"
                variant="outlined"
                error={
                  touched?.soil_type?.field_capacity &&
                  Boolean(errors?.soil_type?.field_capacity)
                }
                helperText={
                  touched?.soil_type?.field_capacity &&
                  errors?.soil_type?.field_capacity
                    ? errors?.soil_type?.field_capacity
                    : ""
                }
              />
            </FormControl>
          </Grid>
        </Fragment>

        <Fragment>
          <Grid item xs={12} sm={12} md={6}>
            <FormControl fullWidth>
              <Field
                type="number"
                inputProps={{ min: 0 }}
                as={TextField}
                label={`${t("soilType." + soilType.texture)} ${t(
                  "soilType.silt"
                )} ${t("soilType.unit.silt")}*`}
                name="soil_type.silt"
                variant="outlined"
                fullWidth
                error={
                  textureSumError ||
                  (touched?.soil_type?.silt && Boolean(errors?.soil_type?.silt))
                }
                helperText={
                  touched?.soil_type?.silt && errors?.soil_type?.silt
                    ? errors?.soil_type?.silt
                    : ""
                }
              />
            </FormControl>
          </Grid>
        </Fragment>

        <Fragment>
          <Grid item xs={12} sm={12} md={6}>
            <FormControl fullWidth>
              <Field
                type="number"
                inputProps={{ min: 0, step: "any" }}
                as={TextField}
                label={`${t("soilType." + soilType.bulk_density)} ${t(
                  "soilType.unit." + soilType.bulk_density
                )}*`}
                name="soil_type.bulk_density"
                variant="outlined"
                error={
                  touched?.soil_type?.bulk_density &&
                  Boolean(errors?.soil_type?.bulk_density)
                }
                helperText={
                  touched?.soil_type?.bulk_density &&
                  errors?.soil_type?.bulk_density
                    ? errors?.soil_type?.bulk_density
                    : ""
                }
              />
            </FormControl>
          </Grid>
        </Fragment>

        <Fragment>
          <Grid item xs={12} sm={12} md={6}>
            <FormControl fullWidth>
              <Field
                type="number"
                inputProps={{ min: 0, step: "any" }}
                as={TextField}
                label={`${t("soilType." + soilType.soil_porosity)} ${t(
                  "soilType.unit." + soilType.soil_porosity
                )}*`}
                name="soil_type.soil_porosity"
                variant="outlined"
                error={
                  touched?.soil_type?.soil_porosity &&
                  Boolean(errors?.soil_type?.soil_porosity)
                }
                helperText={
                  touched?.soil_type?.soil_porosity &&
                  errors?.soil_type?.soil_porosity
                    ? errors?.soil_type?.soil_porosity
                    : ""
                }
              />
              {showSoilPorosityWarning && !errors?.soil_type?.soil_porosity && (
                <Typography
                  variant="caption"
                  sx={{ color: "#ed6c02", mt: 0.5, fontSize: "0.75rem" }}
                >
                  {t("field_form.soil_porosity_warning", { threshold: 35 })}
                </Typography>
              )}
            </FormControl>
          </Grid>
        </Fragment>

        <Fragment>
          <Grid item xs={12} sm={12} md={6}>
            <FormControl fullWidth>
              <Field
                type="number"
                inputProps={{ min: 0, step: "any" }}
                as={TextField}
                label={`${t(
                  "soilType." + soilType.water_content_saturation
                )} ${t("soilType.unit." + soilType.water_content_saturation)}*`}
                name="soil_type.water_content_saturation"
                variant="outlined"
                error={
                  touched?.soil_type?.water_content_saturation &&
                  Boolean(errors?.soil_type?.water_content_saturation)
                }
                helperText={
                  touched?.soil_type?.water_content_saturation &&
                  errors?.soil_type?.water_content_saturation
                    ? errors?.soil_type?.water_content_saturation
                    : ""
                }
              />
            </FormControl>
          </Grid>
        </Fragment>

        <Fragment>
          <Grid item xs={12} sm={12} md={6}>
            <FormControl fullWidth>
              <Field
                type="number"
                inputProps={{ min: 0, step: "any" }}
                as={TextField}
                label={`${t(
                  "soilType." + soilType.water_content_saturation_by_sensor
                )} ${t(
                  "soilType.unit." + soilType.water_content_saturation_by_sensor
                )}*`}
                name="soil_type.water_content_saturation_by_sensor"
                variant="outlined"
                error={
                  touched?.soil_type?.water_content_saturation_by_sensor &&
                  Boolean(errors?.soil_type?.water_content_saturation_by_sensor)
                }
                helperText={
                  touched?.soil_type?.water_content_saturation_by_sensor &&
                  errors?.soil_type?.water_content_saturation_by_sensor
                    ? errors?.soil_type?.water_content_saturation_by_sensor
                    : ""
                }
              />
            </FormControl>
          </Grid>
        </Fragment>

        <Fragment>
          <Grid item xs={12} sm={12} md={6}>
            <FormControl fullWidth>
              <Field
                type="number"
                inputProps={{ min: 0, step: "any" }}
                as={TextField}
                label={`${t(
                  "soilType." + soilType.saturated_hydraulic_conductivity
                )} ${t(
                  "soilType.unit." + soilType.saturated_hydraulic_conductivity
                )}*`}
                name="soil_type.saturated_hydraulic_conductivity"
                variant="outlined"
                error={
                  touched?.soil_type?.saturated_hydraulic_conductivity &&
                  Boolean(errors?.soil_type?.saturated_hydraulic_conductivity)
                }
                helperText={
                  touched?.soil_type?.saturated_hydraulic_conductivity &&
                  errors?.soil_type?.saturated_hydraulic_conductivity
                    ? errors?.soil_type?.saturated_hydraulic_conductivity
                    : ""
                }
              />
            </FormControl>
          </Grid>
        </Fragment>

        <Fragment>
          <Grid item xs={12} sm={12} md={6}>
            <FormControl fullWidth>
              <Field
                type="number"
                inputProps={{ min: 0, step: "any" }}
                as={TextField}
                label={`${t("soilType." + soilType.organic_matter)} ${t(
                  "soilType.unit." + soilType.organic_matter
                )}*`}
                name="soil_type.organic_matter"
                variant="outlined"
                error={
                  touched?.soil_type?.organic_matter &&
                  Boolean(errors?.soil_type?.organic_matter)
                }
                helperText={
                  touched?.soil_type?.organic_matter &&
                  errors?.soil_type?.organic_matter
                    ? errors?.soil_type?.organic_matter
                    : ""
                }
              />
            </FormControl>
          </Grid>
        </Fragment>
      </Grid>
    </Fragment>
  );
};

export default SoilTypeFormComponent;
